import { FileIcon, FolderIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FilePreviewContent } from './FilePreviewContent';
import { useGetSignedDocumentUrl } from '../../../../../../hooks/useGetSignedDocumentUrl';
import type { FileItem } from '../../../../../../types/File/File.types';

interface FilePreviewPanelProps {
    file: FileItem;
    isVisible: boolean;
}

export function FilePreviewPanel({ file, isVisible }: FilePreviewPanelProps) {
    const { t } = useTranslation();

    // Only fetch signed URL for files (not folders) when panel is visible
    const shouldFetchSignedUrl = isVisible && file.type !== 'folder';
    const { data, isLoading, error } = useGetSignedDocumentUrl(
        shouldFetchSignedUrl ? file.id : undefined,
        shouldFetchSignedUrl,
    );

    const renderContent = () => {
        // Handle folder type
        if (file.type === 'folder') {
            return (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                    <FolderIcon className="h-16 w-16 mb-4" />
                    <p>{t('filePreview.folder.notAvailable')}</p>
                    <p className="text-sm mt-2">{t('filePreview.folder.description')}</p>
                </div>
            );
        }

        // Handle loading state
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">{t('filePreview.loading')}</p>
                    </div>
                </div>
            );
        }

        // Handle error state
        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                    <FileIcon className="h-16 w-16 mb-4" />
                    <p>{t('filePreview.errors.loadError')}</p>
                    <p className="text-sm mt-2">{error.message || t('filePreview.errors.unknown')}</p>
                </div>
            );
        }

        // Handle no data state
        if (!data) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                    <FileIcon className="h-16 w-16 mb-4" />
                    <p>{t('filePreview.errors.notAvailable')}</p>
                    <p className="text-sm mt-2">({file.name})</p>
                </div>
            );
        }

        // Render preview content
        return <FilePreviewContent signedUrl={data.signed_url} contentType={data.content_type} />;
    };

    return (
        <div className={`${isVisible ? 'block' : 'hidden md:block'} w-full overflow-auto border rounded-md`}>
            <div className="p-4 border-b">
                <h3 className="font-medium">{t('filePreview.title')}</h3>
            </div>
            <div className="p-4 h-[500px] overflow-auto">{renderContent()}</div>
        </div>
    );
}
